/**
 * Effective permission resolution (preview-aware).
 *
 * Whose flags apply depends on the session mode:
 * - normal session        → the user's own permission
 * - preview, faithful     → the previewed org's flags (currentUser.permission
 *                           IS the previewed org's config — never the real user)
 * - preview, workAsParent → the REAL user's permission (preserved under
 *                           defaultRole): the parent is working on the org's
 *                           behalf with their own capabilities
 *
 * See core/components/orgs/previewAccess.js for the mode computation.
 */

import { PREVIEW_MODES } from '../components/orgs/previewAccess';

/**
 * @param {Object} currentUser – the Apollo currentUser object
 * @returns {Object} the effective permission flags for the session mode
 */
export const getEffectivePermission = (currentUser) => {
  if (!currentUser) return {};
  if (
    currentUser.isPreviewOrg &&
    currentUser.previewMode === PREVIEW_MODES.WORK_AS_PARENT
  ) {
    return currentUser.defaultRole?.permission || {};
  }
  return currentUser.permission || {};
};

/**
 * Some actions must only happen in a real, identified session — never while
 * previewing another org (e.g. creating Salesforce opportunities, which
 * writes external records). True only when the user is a processor in their
 * own session.
 */
export const isRealSessionProcessor = (currentUser) =>
  !!currentUser && !currentUser.isPreviewOrg && !!currentUser.permission?.isProcessor;
